import React, { useState, useEffect } from 'react';
import ApiClient from '../api/client';

interface ConvertKitFieldMappingEditorProps {
  formId: number;
  apiClient: ApiClient;
  hasConvertkitChannels: boolean;
  onMappingUpdated?: () => void;
}

interface FieldMap {
  email?: string;
  first_name?: string;
  [key: string]: string | object | undefined;
  custom_fields?: Record<string, string>;
}

const ConvertKitFieldMappingEditor: React.FC<ConvertKitFieldMappingEditorProps> = ({
  formId,
  apiClient,
  hasConvertkitChannels,
  onMappingUpdated,
}) => {
  const [fieldMap, setFieldMap] = useState<FieldMap>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [newCustomField, setNewCustomField] = useState({ name: '', value: '' });

  useEffect(() => {
    if (hasConvertkitChannels) {
      loadFieldMapping();
    }
  }, [formId, hasConvertkitChannels]);

  const loadFieldMapping = async () => {
    setLoading(true);
    try {
      const result = await apiClient.getConvertkitFieldMapping(formId);
      setFieldMap(result.fieldMap || {});
      setError(null);
    } catch (err) {
      // Field mapping might not exist yet (404), which is fine
      setFieldMap({});
      setError(null);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (value: string) => {
    setFieldMap({ ...fieldMap, email: value });
    setIsDirty(true);
  };

  const handleFirstNameChange = (value: string) => {
    if (value === '') {
      const updated = { ...fieldMap };
      delete updated.first_name;
      setFieldMap(updated);
    } else {
      setFieldMap({ ...fieldMap, first_name: value });
    }
    setIsDirty(true);
  };

  const handleCustomFieldChange = (key: string, value: string) => {
    const customFields = fieldMap.custom_fields || {};
    if (value === '') {
      const updated = { ...customFields };
      delete updated[key];
      setFieldMap({
        ...fieldMap,
        custom_fields: Object.keys(updated).length > 0 ? updated : undefined,
      });
    } else {
      setFieldMap({
        ...fieldMap,
        custom_fields: { ...customFields, [key]: value },
      });
    }
    setIsDirty(true);
  };

  const handleAddCustomField = () => {
    if (!newCustomField.name.trim() || !newCustomField.value.trim()) {
      setError('Please enter both field name and mapping');
      return;
    }

    const customFields = fieldMap.custom_fields || {};
    if (customFields[newCustomField.name]) {
      setError('This custom field already exists');
      return;
    }

    setFieldMap({
      ...fieldMap,
      custom_fields: { ...customFields, [newCustomField.name]: newCustomField.value },
    });
    setNewCustomField({ name: '', value: '' });
    setIsDirty(true);
    setError(null);
  };

  const handleSave = async () => {
    if (!fieldMap.email) {
      setError('Email field mapping is required');
      return;
    }

    setSaving(true);
    try {
      await apiClient.setConvertkitFieldMapping(formId, fieldMap);
      setIsDirty(false);
      setError(null);
      onMappingUpdated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save field mapping');
    } finally {
      setSaving(false);
    }
  };

  if (!hasConvertkitChannels) {
    return null;
  }

  if (loading) {
    return (
      <div className="details-section">
        <h4>🔗 ConvertKit Field Mapping</h4>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <div className="loading-spinner"></div>
          <p>Loading field mapping...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="details-section">
      <h4>🔗 ConvertKit Field Mapping</h4>
      {error && (
        <div className="alert alert-error" style={{ marginBottom: '12px' }}>
          <span>⚠️</span>
          <span>{error}</span>
        </div>
      )}

      <div style={{ backgroundColor: '#f5f5f5', padding: '12px', borderRadius: '4px', marginBottom: '16px' }}>
        <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#666' }}>
          Map your form fields to ConvertKit fields. This mapping is shared across all ConvertKit channels attached to this form.
        </p>
      </div>

      <div className="form-group">
        <label>Email Field * (required)</label>
        <input
          type="text"
          value={fieldMap.email || ''}
          onChange={(e) => handleEmailChange(e.target.value)}
          placeholder="e.g., email_address, user_email"
          disabled={saving}
          style={{ fontFamily: 'monospace' }}
        />
        <small>The form field that contains the subscriber's email</small>
      </div>

      <div className="form-group">
        <label>First Name Field (optional)</label>
        <input
          type="text"
          value={fieldMap.first_name || ''}
          onChange={(e) => handleFirstNameChange(e.target.value)}
          placeholder="e.g., first_name, fname"
          disabled={saving}
          style={{ fontFamily: 'monospace' }}
        />
        <small>Direct field on ConvertKit subscriber (separate from custom fields)</small>
      </div>

      {fieldMap.custom_fields && Object.keys(fieldMap.custom_fields).length > 0 && (
        <div className="form-group">
          <label>Custom Fields</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {Object.entries(fieldMap.custom_fields).map(([key, value]) => (
              <div key={key} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="text"
                  value={key}
                  disabled
                  style={{ flex: 1, fontFamily: 'monospace' }}
                />
                <span style={{ fontSize: '18px', color: '#ccc' }}>→</span>
                <input
                  type="text"
                  value={value}
                  onChange={(e) => handleCustomFieldChange(key, e.target.value)}
                  disabled={saving}
                  style={{ flex: 1, fontFamily: 'monospace' }}
                />
                <button
                  type="button"
                  onClick={() => handleCustomFieldChange(key, '')}
                  disabled={saving}
                  style={{
                    padding: '6px 8px',
                    background: '#ff6b6b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                  }}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="form-group">
        <label>Add Custom Field</label>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
          <input
            type="text"
            value={newCustomField.name}
            onChange={(e) => setNewCustomField({ ...newCustomField, name: e.target.value })}
            placeholder="ConvertKit field name"
            disabled={saving}
            style={{ flex: 1, fontFamily: 'monospace' }}
          />
          <span style={{ fontSize: '18px', color: '#ccc', alignSelf: 'center' }}>→</span>
          <input
            type="text"
            value={newCustomField.value}
            onChange={(e) => setNewCustomField({ ...newCustomField, value: e.target.value })}
            placeholder="Form field name"
            disabled={saving}
            style={{ flex: 1, fontFamily: 'monospace' }}
          />
        </div>
        <button
          type="button"
          onClick={handleAddCustomField}
          disabled={saving}
          style={{
            padding: '6px 12px',
            background: '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontSize: '13px',
          }}
        >
          ➕ Add Field
        </button>
      </div>

      {isDirty && (
        <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
          <button
            onClick={handleSave}
            disabled={saving || !fieldMap.email}
            style={{
              flex: 1,
              padding: '8px 12px',
              background: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: saving || !fieldMap.email ? 'not-allowed' : 'pointer',
              fontWeight: '500',
            }}
          >
            {saving ? '⏳ Saving...' : '✓ Save Mapping'}
          </button>
          <button
            onClick={loadFieldMapping}
            disabled={saving}
            style={{
              padding: '8px 12px',
              background: '#e0e0e0',
              color: '#333',
              border: 'none',
              borderRadius: '4px',
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            ✕ Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default ConvertKitFieldMappingEditor;
